import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { FeatureGroup, Polygon, Tooltip } from "react-leaflet";
import { useTheme } from "@material-ui/core/styles";

import { GeofencesContext } from "../../context";
import { GeofencesHollowLayers, MinVisibleHexesZoom } from "../../constants";
import { RefType, ZoneType, LayerType } from "./types";
import { isPointPolygon, getPolygonColor } from "../../services/geofences.service";

const Geofence = ({ zone, zoom, bounds, polygonsRef, groupPolygonsRef, layer, sidewalksMode }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { showPolygons, selectedLayer, selectedZone, selectZone, showHexes } =
    React.useContext(GeofencesContext);

  const color = getPolygonColor(theme, layer.type);

  const onSelectZone = (key, parent) => {
    if (selectedZone.editMode) return;
    selectZone({ key, parent });
  };

  const getZoneOpacity = (type, key) => {
    if (GeofencesHollowLayers.includes(type)) return 0;

    return key === selectedZone.key ? 0.4 : 0.2;
  };

  const getZoneBorderOpacity = ({ active, key }) => {
    if (!active) return 0.4;

    return !selectedZone.key || selectedZone.key === key ? 1 : 0.6;
  };

  const shouldRenderHexes =
    showHexes &&
    bounds &&
    (selectedZone.key === zone.key ||
      !sidewalksMode ||
      (zoom > MinVisibleHexesZoom && !selectedZone.key));

  return (
    <FeatureGroup
      ref={(node) => {
        groupPolygonsRef.current[zone.key] = node;
      }}
      onClick={() => onSelectZone(zone.key, layer.key)}
    >
      {showPolygons &&
        (zone.polygon || zone.generatedPolygon) &&
        (!sidewalksMode || !selectedZone.key || selectedZone.key === zone.key) && (
          <Polygon
            ref={(node) => {
              polygonsRef.current[zone.key] = node;
            }}
            positions={zone.polygon || zone.generatedPolygon}
            color={color}
            fillOpacity={getZoneOpacity(layer.type, zone.key)}
            opacity={getZoneBorderOpacity(zone)}
            weight={isPointPolygon(zone.polygon) ? 8 : 2}
          >
            {!selectedZone.key && !selectedLayer.key && (
              <Tooltip opacity={0.75}>
                {zone.name || t("Fleet.Geofences.ZoneName")}
                {`, ${t(`Fleet.Geofences.LayerTypes.${layer.type}`)}`}
                {layer.speed ? `, ${layer.speed} km/h` : ""}
              </Tooltip>
            )}
          </Polygon>
        )}
      {shouldRenderHexes &&
        Object.values(zone.hexes).map(
          (hex) =>
            (selectedZone.key === zone.key ||
              hex.polygon.some((latlng) => bounds.contains(latlng))) && (
              <Polygon
                ref={(node) => {
                  polygonsRef.current[hex.polygon.h3Index] = node;
                }}
                key={hex.h3Index}
                h3Index={hex.h3Index}
                positions={hex.polygon}
                color={color}
                opacity={zone.active ? 1 : 0.4}
              />
            ),
        )}
    </FeatureGroup>
  );
};

Geofence.propTypes = {
  zone: ZoneType.isRequired,
  zoom: PropTypes.number.isRequired,
  polygonsRef: RefType.isRequired,
  groupPolygonsRef: RefType.isRequired,
  bounds: PropTypes.object.isRequired,
  layer: LayerType.isRequired,
  sidewalksMode: PropTypes.bool,
};

Geofence.defaultProps = {
  sidewalksMode: false,
};

export default Geofence;
